import AsyncStorage from './StorageHandler';
import {isObject} from '../utils/Helpers';
import {MILITARY_STATUSES} from '../../shared/constants/MilitaryStatusSlugs';
import { projectConfig } from '../project-info/projectConfig';
import { userLocation } from './UserLocation';

class UserProfile {
  newsletter_subscription = [];
  anchor_chapter = {};
  anonymous_profile = false;
  combat_deployment_operations = null;
  combat_zone = null;
  do_not_send_any_email = null;
  eagle_leader = false;
  eagle_leader_title = null;
  email = null;
  email_verified = null;
  first_name = null;
  gender = null;
  gender_description = null;
  has_disability = null;
  id = null;
  last_name = null;
  legal_waiver_signed = null;
  location = {};
  military_branch = null;
  military_family_member = null;
  military_status = null;
  military_specialty = null;
  military_rank = null;
  military_ets = null;
  phone = null;
  preferred_chapter = {};
  profile_completed = null;
  profile_photo_url = null;
  cover_photo_url = null;
  profile_bio = null;
  shipping_location = {};
  shirt_size = null;
  registration_started_via_app = false;
  salesforce_contact_id = null;
  service_info = null;
  extra_info = null;
  latest_purchase= null;
  workspace = null;
  isOperator = false;
  constructor() {
    this.dataLoaded = false;
    this.saveToUserProfile = this.saveToUserProfile.bind(this);
    this.setUserId = this.setUserId.bind(this);
    this._loadUserProfile = this._loadUserProfile.bind(this);
    this.init = this.init.bind(this);
  }
  _setUserProfile(data) {
    const {
      newsletter_subscription,
      anchor_chapter,
      anonymous_profile,
      combat_deployment_operations,
      combat_zone,
      do_not_send_any_email,

      // eagle_leader is a confusing concept in the whitelabel. It's leftover from RWB.
      // TODO: rename to event_leader
      // Furthermore, the backend currently has three ideas for this: eagle_leader, event_leader (or just leader), and the event_leader permissions group.
      // But in the end, it always just checks the permissions group to set the value anyway.

      // the client actually checks for eagle_leader OR event_leader permissions to set this bool.
      // it would be nice to clean it all up so there's one idea of event_leader.
      // there's also a confusing projectConfig setting with additional comments.
      
      eagle_leader,
      eagle_leader_title,
      email,
      email_verified,
      first_name,
      gender,
      gender_description,
      has_disability,
      id,
      last_name,
      legal_waiver_signed,
      location,
      military_branch,
      military_family_member,
      military_status,
      military_specialty,
      military_rank,
      military_ets,
      permissions,
      phone,
      preferred_chapter,
      profile_completed,
      profile_photo_url,
      cover_photo_url,
      profile_bio,
      shipping_location,
      shirt_size,
      registration_started_via_app,
      salesforce_contact_id,
      service_info,
      extra_info,
      latest_purchase,
      workspace,
      isOperator,
    } = data;
    let hasPermissions;
    if (permissions) {
      hasPermissions = permissions.includes(projectConfig.leaderRole);
    }
    this.newsletter_subscription = newsletter_subscription;
    this.anchor_chapter = anchor_chapter;
    this.anonymous_profile = anonymous_profile;
    this.combat_deployment_operations = combat_deployment_operations;
    this.combat_zone = combat_zone;
    this.do_not_send_any_email = do_not_send_any_email;
    this.eagle_leader = Boolean(eagle_leader || hasPermissions);
    this.eagle_leader_title = eagle_leader_title;
    this.email = email;
    this.email_verified = email_verified;
    this.first_name = first_name;
    this.gender = gender;
    this.gender_description = gender_description
    this.has_disability = has_disability;
    this.id = id;
    this.last_name = last_name;
    this.legal_waiver_signed = legal_waiver_signed;
    this.location = location;
    this.military_branch = military_branch;
    this.military_family_member = military_family_member;
    this.military_status = military_status;
    this.military_specialty = military_specialty;
    this.military_rank = military_rank;
    this.military_ets = military_ets;
    this.phone = phone;
    this.preferred_chapter = preferred_chapter;
    this.profile_completed = profile_completed;
    this.profile_photo_url = profile_photo_url;
    this.cover_photo_url = cover_photo_url;

    this.profile_bio = profile_bio;
    this.shipping_location = shipping_location;
    this.shirt_size = shirt_size;
    this.registration_started_via_app = registration_started_via_app;
    this.salesforce_contact_id = salesforce_contact_id;
    this.service_info = service_info;
    this.extra_info = extra_info;
    this.latest_purchase= latest_purchase;
    this.workspace = workspace;
    this.isOperator = isOperator || permissions?.includes('operator');
    return this.getUserProfile();
  }
  init() {
    if (!this.dataLoaded) {
      this.dataLoaded = true;
      return this._loadUserProfile();
    } else {
      return Promise.resolve(this.getUserProfile());
    }
  }
  setUserId(id) {
    this.id = id;
  }
  saveToUserProfile(value) {
    if (!value || !isObject(value))
      throw new TypeError('input must be of type Object');

    // also save to userLocation if location data is present
    if (value.location) {
      const locationData = {
        street: value.location.address,
        city: value.location.city,
        state: value.location.address_state,
        zip: value.location.zip,
        // profile doesn't have lat/long. Avoid a UserLocation with mismatched address and lat/long.
        lat: undefined,
        long: undefined,
      };
      userLocation.saveToUserLocation(locationData).catch(error => {
        console.error('Error saving profile location to userLocation:', error);
      });
    }

    this._setUserProfile(value);
    return new Promise((resolve) => {
      AsyncStorage.setItem('user_profile', JSON.stringify(value)).then(resolve);
    });
  }
  _loadUserProfile() {
    return AsyncStorage.getItem('user_profile').then((data) => {
      if (data) {
        return this._setUserProfile(JSON.parse(data));
      } else {
        throw new Error();
      }
    });
  }
  getUserProfile() {
    const {
      newsletter_subscription,
      anchor_chapter,
      anonymous_profile,
      combat_deployment_operations,
      combat_zone,
      do_not_send_any_email,
      eagle_leader,
      eagle_leader_title,
      email,
      email_verified,
      first_name,
      gender,
      gender_description,
      has_disability,
      last_name,
      legal_waiver_signed,
      location,
      military_branch,
      military_family_member,
      military_status,
      military_specialty,
      military_rank,
      military_ets,
      phone,
      preferred_chapter,
      profile_completed,
      profile_photo_url,
      cover_photo_url,
      profile_bio,
      shipping_location,
      shirt_size,
      id,
      registration_started_via_app,
      salesforce_contact_id,
      service_info,
      extra_info,
      latest_purchase,
      isOperator,
      workspace,
    } = this;
    return {
      newsletter_subscription,
      anchor_chapter,
      anonymous_profile,
      combat_deployment_operations,
      combat_zone,
      do_not_send_any_email,
      eagle_leader,
      eagle_leader_title,
      email,
      email_verified,
      first_name,
      gender,
      gender_description,
      has_disability,
      last_name,
      legal_waiver_signed,
      location,
      military_branch,
      military_family_member,
      military_status,
      military_specialty,
      military_rank,
      military_ets,
      phone,
      preferred_chapter,
      profile_completed,
      profile_photo_url,
      cover_photo_url,
      profile_bio,
      shipping_location,
      shirt_size,
      id,
      registration_started_via_app,
      salesforce_contact_id,
      service_info,
      extra_info,
      latest_purchase,
      isOperator,
      workspace,
    };
  }

  /**
   * Generates a payload for use with posting to the API server.
   */
  formatPayload(patch) {
    let data = Object.assign({}, this, patch);

    const {
      anchor_chapter,
      anonymous_profile,
      combat_deployment_operations,
      combat_zone,
      do_not_send_any_email,
      eagle_leader_title,
      eagle_leader,
      email_verified,
      email,
      military_ets,
      first_name,
      gender,
      gender_description,
      has_disability,
      id,
      last_name,
      legal_waiver_signed,
      location,
      military_branch,
      military_family_member,
      military_status,
      military_rank,
      military_specialty,
      newsletter_subscription,
      phone,
      preferred_chapter,
      profile_bio,
      profile_completed,
      profile_photo_url,
      cover_photo_url,
      registration_started_via_app,
      shipping_location,
      shirt_size,
      service_info,
      extra_info,
      latest_purchase,
      workspace,
    } = data;

    let payload = {
      // Personal Info
      first_name: first_name,
      last_name: last_name,
      email: email,
      gender: gender,
      gender_description: gender_description,

      address_type: location.address_type,
      country: location.country,
      state: location.address_state,
      city: location.city,
      zipcode: location.zip,
      street: location.address,
      street_2: location.address_2,

      phone: phone,

      // Social profile
      profile_bio: profile_bio,

      // Military Service
      military_status: military_status,

      // Legal Waiver
      legal_waiver_signed: legal_waiver_signed,
      anonymous_profile: anonymous_profile,
      profile_completed: profile_completed,

      // Red Shirt
      shirt_size: shirt_size,

      service_info: service_info,
      extra_info: extra_info,
      latest_purchase: latest_purchase,
    };

    if (military_status === MILITARY_STATUSES.civilian) {
      payload.military_family_member = military_family_member;
    } else {
      payload.military_branch = military_branch;
      payload.military_specialty = military_specialty;
      payload.military_rank = military_rank;
      payload.has_disability = has_disability;
      payload.combat_zone = combat_zone;
      payload.combat_deployment_operations = combat_deployment_operations;
    }
    if (military_status === MILITARY_STATUSES.veteran) {
      payload.military_ets = military_ets;
    }
    if (shipping_location.shipping_address) {
      payload.shipping_address = true;
      payload.shipping_country = shipping_location.country;
      payload.shipping_state = shipping_location.address_state;
      payload.shipping_city = shipping_location.city;
      payload.shipping_zip = shipping_location.zip;
      payload.shipping_street = shipping_location.address;
    } else {
      payload.shipping_address = false;
    }

    return payload;
  }
  deleteUserProfile() {
    return AsyncStorage.removeItem('user_profile');
  }
}

export let userProfile = new UserProfile();
